import { sanctumTokenStorage } from './utils/sanctumTokenStorage'

const overlayLayer = 'z-(--z-index-ds-modal)'
const floatingLayer = { slots: { content: 'z-(--z-index-ds-dropdown)' } }
const overlayContent =
  'bg-surface-canvas dark:bg-surface-level-900 dark:divide-border-inverse/10 divide-border-level-100'
const overlayTitle = 'dark:text-content-inverse font-semibold text-content-level-900'
const overlayDescription = 'dark:text-content-level-400 mt-1 text-content-level-500 text-sm'
// `static` makes the close button a flex child of the header, not an overlay.
const overlayClose =
  'border border-border-level-200 cursor-pointer dark:border-border-inverse/10 dark:hover:bg-surface-canvas/10 dark:hover:text-content-inverse dark:text-content-level-400 flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 h-8 hover:bg-surface-level-100 hover:text-content-level-700 inline-flex items-center justify-center rounded-full static text-content-level-500 transition w-8'

export default defineAppConfig({
  ui: {
    colors: {
      primary: 'primary',
      neutral: 'slate',
      success: 'success',
      info: 'info',
      warning: 'warning',
      error: 'danger'
    },
    modal: {
      slots: {
        // The Mealz modal skin, owned here rather than in BaseModal's markup, so
        // the panel, header, body, footer and close button come from the theme.
        overlay: `backdrop-blur-[2px] ${overlayLayer}`,
        content: `${overlayContent} ${overlayLayer}`,
        header: 'gap-4 items-start px-6 py-5',
        wrapper: 'flex-1 min-w-0',
        body: 'px-6 py-5 sm:py-5',
        footer: 'gap-3 justify-end px-6 py-4',
        // Holds an optional leading icon beside the heading text.
        title: `flex gap-3 items-center text-lg ${overlayTitle}`,
        description: overlayDescription,
        close: overlayClose
      },
      variants: {
        // Replaces the vendor scrim, which `tv` applies after the base slot.
        overlay: {
          true: { overlay: 'bg-surface-level-950/55' }
        },
        // Full-screen modals reach every edge, so the frame applies to windowed ones only.
        fullscreen: {
          false: {
            content:
              'border border-border-level-100 dark:border-border-inverse/10 rounded-2xl shadow-xl'
          }
        }
      }
    },
    slideover: {
      slots: {
        overlay: overlayLayer,
        // The Mealz drawer skin, owned here rather than hand-written in
        // BaseDrawer's markup, so the panel, header, body, footer and close
        // button come from the theme once instead of once per shell branch.
        content: `${overlayContent} shadow-2xl ${overlayLayer}`,
        header: 'gap-3 items-start px-5 py-4',
        wrapper: 'flex-1 min-w-0',
        body: 'p-5 sm:p-5',
        footer: 'gap-3 justify-end px-5 py-4 sm:px-5',
        title: `text-lg ${overlayTitle}`,
        description: overlayDescription,
        close: overlayClose
      }
    },
    popover: floatingLayer,
    dropdownMenu: floatingLayer,
    contextMenu: floatingLayer,
    tooltip: floatingLayer,
    selectMenu: floatingLayer,
    inputMenu: floatingLayer,
    select: {
      slots: floatingLayer.slots,
      variants: {
        variant: {
          // Greys the surface and text; Nuxt UI's opacity-only disabled state reads as enabled.
          outline: 'disabled:bg-elevated disabled:text-dimmed'
        }
      }
    },
    badge: {
      variants: {
        variant: {
          // Empty: the palette comes from `color` below, not a vendor colour-by-variant pair.
          none: ''
        },
        size: {
          pillXs: { base: 'font-semibold px-2 py-0.5 rounded-full text-[11px]' },
          pillXsMedium: { base: 'font-medium px-2 py-0.5 rounded-full text-[11px]' },
          pillXsLoose: { base: 'font-medium px-2 py-0.5 rounded-full text-xs' },
          pillXsCaps: {
            base: 'font-semibold px-2 py-0.5 rounded-full text-[10px] tracking-[0.08em] uppercase'
          },
          pillSm: { base: 'font-semibold px-2.5 py-1 rounded-full text-[11px]' },
          pillSmMedium: { base: 'font-medium px-2.5 py-1 rounded-full text-[11px]' },
          pillSmIcon: { base: 'font-semibold gap-1.5 px-2.5 py-1 rounded-full text-[11px]' },
          pillSmCaps: {
            base: 'font-semibold px-2.5 py-1 rounded-full text-[11px] tracking-[0.08em] uppercase'
          },
          pillSmCapsTight: {
            base: 'font-semibold px-2.5 py-1 rounded-full text-[10px] tracking-[0.08em] uppercase'
          },
          pillMdCaps: {
            base: 'font-semibold gap-2 px-3 py-1 rounded-full text-[11px] tracking-[0.08em] uppercase'
          },
          pillLg: { base: 'font-semibold gap-2 px-3 py-1.5 rounded-full text-xs' },
          // Empty: geometry stays at the call site; omitting `size` would leak the stock `md` default's `gap-1`.
          pillCustom: { base: '' }
        },
        color: {
          mealzSuccess:
            'bg-success-50 border border-success-200 dark:bg-success-500/10 dark:border-success-400/20 dark:text-success-300 text-success-700',
          mealzNeutral:
            'bg-surface-level-50 border border-border-level-200 dark:bg-surface-canvas/5 dark:border-border-inverse/10 dark:text-content-level-300 text-content-level-600',
          mealzMuted:
            'bg-surface-level-100 dark:bg-surface-canvas/10 dark:text-content-level-300 text-content-level-600',
          mealzPrimary: 'border border-primary/20 text-primary',
          mealzPrimarySoft: 'bg-primary/10 border border-primary/20 text-primary',
          mealzCanvas:
            'backdrop-blur-sm bg-surface-canvas/80 border border-border-level-300/80 dark:bg-surface-canvas/10 dark:border-border-inverse/15 dark:text-content-level-200 text-content-level-700'
        }
      }
    }
  },
  sanctum: {
    tokenStorage: sanctumTokenStorage
  }
})
